import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { VehicleBooking, VehicleBookingWithFieldValues, BookingStatus } from '../types/vehicle';

const COLLECTION_NAME = 'vehicleBookings';

/**
 * Create a new vehicle booking
 */
export const createBooking = async (
  bookingData: Omit<VehicleBookingWithFieldValues, 'createdAt' | 'updatedAt'>,
  organizationId: string
): Promise<string> => {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...bookingData,
    organizationId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Get a single booking by ID
 */
export const getBooking = async (id: string): Promise<VehicleBooking | null> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as VehicleBooking;
  }
  return null;
};

/**
 * Get all bookings for an organization with optional filters
 */
export const getBookings = async (
  organizationId: string,
  filters?: {
    vehicleId?: string;
    status?: BookingStatus;
    startDate?: Date;
    endDate?: Date;
  }
): Promise<VehicleBooking[]> => {
  const constraints: QueryConstraint[] = [
    where('organizationId', '==', organizationId),
    orderBy('startDate', 'desc'),
  ];

  if (filters?.vehicleId) {
    constraints.push(where('vehicleId', '==', filters.vehicleId));
  }

  if (filters?.status) {
    constraints.push(where('status', '==', filters.status));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VehicleBooking[];
};

/**
 * Get bookings for a specific vehicle in a date range
 */
export const getVehicleBookingsInRange = async (
  vehicleId: string,
  startDate: Date,
  endDate: Date,
  organizationId: string
): Promise<VehicleBooking[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('vehicleId', '==', vehicleId),
    where('status', 'in', ['pending', 'confirmed', 'in_progress']),
    where('startDate', '<=', Timestamp.fromDate(endDate)),
    where('endDate', '>=', Timestamp.fromDate(startDate))
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VehicleBooking[];
};

/**
 * Check if a vehicle is available for booking in a date range
 */
export const isVehicleAvailable = async (
  vehicleId: string,
  startDate: Date,
  endDate: Date,
  organizationId: string,
  excludeBookingId?: string
): Promise<boolean> => {
  const bookings = await getVehicleBookingsInRange(vehicleId, startDate, endDate, organizationId);
  
  // Filter out the booking being edited if provided
  const conflictingBookings = excludeBookingId
    ? bookings.filter(b => b.id !== excludeBookingId)
    : bookings;
  
  return conflictingBookings.length === 0;
};

/**
 * Update a booking
 */
export const updateBooking = async (
  id: string,
  updates: Partial<VehicleBookingWithFieldValues>
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Update booking status
 */
export const updateBookingStatus = async (
  id: string,
  status: BookingStatus,
  userId?: string
): Promise<void> => {
  const updates: any = {
    status,
    updatedAt: serverTimestamp(),
  };

  if (status === 'confirmed' && userId) {
    updates.confirmedBy = userId;
    updates.confirmedAt = serverTimestamp();
  }

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, updates);
};

/**
 * Delete a booking
 */
export const deleteBooking = async (id: string): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
};

/**
 * Get upcoming bookings (next 30 days)
 */
export const getUpcomingBookings = async (
  organizationId: string,
  days: number = 30
): Promise<VehicleBooking[]> => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + days);

  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('status', 'in', ['pending', 'confirmed']),
    where('startDate', '>=', Timestamp.fromDate(today)),
    where('startDate', '<=', Timestamp.fromDate(futureDate)),
    orderBy('startDate', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VehicleBooking[];
};

/**
 * Get active bookings (currently in progress)
 */
export const getActiveBookings = async (
  organizationId: string
): Promise<VehicleBooking[]> => {
  const today = new Date();

  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('status', '==', 'in_progress'),
    where('startDate', '<=', Timestamp.fromDate(today)),
    where('endDate', '>=', Timestamp.fromDate(today)),
    orderBy('startDate', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VehicleBooking[];
};

/**
 * Get bookings by customer email
 */
export const getBookingsByCustomerEmail = async (
  email: string,
  organizationId: string
): Promise<VehicleBooking[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('organizationId', '==', organizationId),
    where('userEmail', '==', email),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as VehicleBooking[];
};
